import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { PageHeader, Tag, StatCard } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Star, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const isInactiveUser = (u) => ["deactivated", "inactive"].includes(u.status);

const CRITERIA_LABELS = {
  urgency: "Urgency", accountability: "Accountability", initiative: "Initiative",
  studio_alignment: "Studio alignment", client_leadership: "Client leadership",
  process_discipline: "Process & discipline", collaboration: "Collaboration",
  work_quality: "Work quality", autonomy: "Autonomy",
};

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function scoreTone(v) {
  if (v >= 4) return "green";
  if (v >= 2) return "orange";
  return "red";
}

function buAccent(avg) {
  if (avg == null) return "text-slate-500";
  if (avg >= 4) return "text-green-700";
  if (avg >= 2) return "text-orange-700";
  return "text-red-700";
}

export default function PerformanceReviews() {
  const { user } = useAuth();
  const can = usePermissions();
  const isAdmin = can("performance_reviews");
  const [criteria, setCriteria] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [users, setUsers] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [buStats, setBuStats] = useState(null);
  const [target, setTarget] = useState(isAdmin ? "" : user?.id);
  const [bu, setBu] = useState("ALL");
  const [period, setPeriod] = useState(thisMonth());
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isAdmin) api.get("/users", { params: { kind: "internal", include_inactive: showInactive } }).then((r) => setUsers(r.data)).catch(() => {});
    if (isAdmin) api.get("/performance-reviews/bu-summary").then((r) => setBuStats(r.data)).catch(() => {});
  }, [isAdmin, showInactive]);

  const load = useCallback(async () => {
    if (isAdmin && !target) { setReviews([]); setScores({}); setNotes(""); return; }
    const params = isAdmin && target ? { user_id: target } : {};
    try {
      const { data } = await api.get("/performance-reviews", { params });
      setCriteria(data.criteria); setReviews(data.reviews);
      // Pre-fill current period draft from latest existing review if same period
      const existing = (data.reviews || []).find((r) => r.period === period);
      setScores(existing?.scores || {});
      setNotes(existing?.notes || "");
    } catch (e) { toast.error(formatApiError(e)); }
  }, [isAdmin, target, period]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!target) { toast.error("Pick a team member"); return; }
    setSaving(true);
    try {
      await api.post("/performance-reviews", { user_id: target, period, scores, notes });
      toast.success("Review saved");
      load();
      if (isAdmin) api.get("/performance-reviews/bu-summary").then((r) => setBuStats(r.data));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const reset = () => {
    setScores({});
    setNotes("");
  };

  const setScore = (k, v) => setScores((s) => ({ ...s, [k]: Number(v) }));

  const avg = useMemo(() => {
    const vals = Object.values(scores).filter((v) => typeof v === "number");
    if (!vals.length) return null;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
  }, [scores]);

  const targetUser = users.find((u) => u.id === target);

  const filteredUsers = useMemo(() => {
    if (!isAdmin) return [];
    return bu === "ALL" ? users : users.filter((u) => u.business_unit === bu);
  }, [users, bu, isAdmin]);

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto fade-in" data-testid="performance-page">
      <PageHeader
        eyebrow="Performance"
        title="Monthly reviews"
        subtitle="Score 9 criteria on a 0–5 scale. One review per person per month."
      />

      {isAdmin && buStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Studio average" value={buStats.studio_avg ?? "—"} hint={`${buStats.reviewed_this_month} of ${buStats.total_members} reviewed`} accent="text-[#F54800]" />
          {(buStats.by_bu || []).map((b) => (
            <StatCard key={b.bu} label={`${b.bu} avg`} value={b.average ?? "—"} hint={`${b.reviewed}/${b.total} members`} accent={buAccent(b.average)} />
          ))}
        </div>
      )}

      {isAdmin && (
        <div className="card-flat p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="label-overline">Filter by BU</Label>
              <Select value={bu} onValueChange={setBu}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="perf-bu-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All BUs</SelectItem>
                  <SelectItem value="BU1">BU1</SelectItem>
                  <SelectItem value="BU2">BU2</SelectItem>
                  <SelectItem value="BU3">BU3</SelectItem>
                  <SelectItem value="Operations">Operations</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between">
                <Label className="label-overline">Team member</Label>
                <div className="flex items-center gap-2">
                  <Switch id="perf-show-inactive" checked={showInactive} onCheckedChange={setShowInactive} data-testid="perf-show-inactive-toggle" />
                  <Label htmlFor="perf-show-inactive" className="text-xs text-slate-500 cursor-pointer">Show inactive</Label>
                </div>
              </div>
              <Select value={target || "none"} onValueChange={(v) => setTarget(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="perf-user-pick"><SelectValue placeholder="Pick someone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Pick someone —</SelectItem>
                  {filteredUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role.replace(/_/g, " ")} · {u.business_unit || "—"}{isInactiveUser(u) && <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Inactive</span>}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-overline">Period</Label>
              <Input type="month" className="rounded-sm mt-1" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="perf-period" />
            </div>
          </div>
        </div>
      )}

      {(isAdmin && target) || (!isAdmin) ? (
        <div className="card-flat p-6 mb-8" data-testid="perf-scoring-panel">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="label-overline text-[#F54800]">{period}</div>
              <div className="font-display text-2xl font-bold text-slate-900">{targetUser?.name || user?.name}</div>
              <div className="text-xs text-slate-500">{targetUser?.title || user?.title} · {targetUser?.business_unit || user?.business_unit || "—"}</div>
            </div>
            <div className="text-right">
              <div className="label-overline">Average</div>
              <div className="font-display text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
                <Star size={20} className="text-[#F54800] fill-[#F54800]" />
                {avg ?? "—"} <span className="text-sm text-slate-400">/ 5</span>
              </div>
            </div>
          </div>

          {isAdmin ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {(criteria || []).map((k) => (
                  <div key={k} className="flex items-center justify-between gap-3 border-b border-slate-100 py-2">
                    <div className="text-sm text-slate-700">{CRITERIA_LABELS[k] || k}</div>
                    <div className="flex items-center gap-1">
                      {[0,1,2,3,4,5].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setScore(k, s)}
                          className={`w-7 h-7 text-xs rounded-sm border transition-colors ${scores[k] === s ? "bg-[#F54800] text-white border-[#F54800]" : "bg-white text-slate-600 border-slate-200 hover:border-[#F54800]"}`}
                          data-testid={`perf-${k}-${s}`}
                        >{s}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Label className="label-overline">Notes (optional)</Label>
                <Textarea rows={3} className="rounded-sm mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="perf-notes" />
              </div>
              <div className="flex items-center justify-end gap-2 mt-4">
                <Button variant="outline" onClick={reset} className="rounded-sm" data-testid="perf-reset"><RotateCcw size={14} className="mr-1.5" />Reset</Button>
                <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="perf-save"><Save size={14} className="mr-1.5" />Save review</Button>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500 italic">Only admins can score reviews. Your history is shown below.</p>
          )}
        </div>
      ) : (
        <div className="card-flat p-8 text-center text-sm text-slate-500" data-testid="perf-empty">
          Pick a team member above to start a review. {buStats?.reviewed_this_month ?? 0} of {users.length} reviewed this month.
        </div>
      )}

      <div className="space-y-3">
        <div className="label-overline">History</div>
        {reviews.map((r) => (
          <div key={r.id} className="card-flat p-5" data-testid={`review-row-${r.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="label-overline">{r.period}</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-1 inline-flex items-center gap-2">
                  <Star size={20} className="text-[#F54800] fill-[#F54800]" />{r.average} <span className="text-sm text-slate-400">/ 5</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">Reviewed by {r.reviewer_name}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4">
              {Object.entries(r.scores || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border border-slate-100 rounded-sm px-3 py-1.5">
                  <span className="text-xs text-slate-700">{CRITERIA_LABELS[k] || k}</span>
                  <Tag tone={scoreTone(v)}>{v}</Tag>
                </div>
              ))}
            </div>
            {r.notes && <p className="text-sm text-slate-600 mt-3 italic">&ldquo;{r.notes}&rdquo;</p>}
          </div>
        ))}
        {reviews.length === 0 && isAdmin && target && (
          <div className="card-flat p-5 text-sm text-slate-500">No prior reviews for this person.</div>
        )}
        {reviews.length === 0 && !isAdmin && (
          <div className="card-flat p-5 text-sm text-slate-500">No reviews yet.</div>
        )}
      </div>
    </div>
  );
}
